import { useTheme } from '../../system';
import { arrayToObjectNotation, fromEntries, isArray, isCustomBreakpoint } from '../../utils';
import { getClosestValue } from './media-query.utils';
import { useBreakpoint } from './use-breakpoint';
/**
 * This returns a value depending on the current screen size, and the theme's
 * breakpoints.
 *
 * The value for each breakpoint will be used for that screen size and all
 * sizes above it, until another breakpoint is specified. For example, if the
 * theme's breakpoints are 'sm', 'md', 'lg', 'xl' and '2xl', this
 * ```
 * useBreakpointValue({ base: '150px', md: '250px', '2xl': '400px' })
 * ```
 * would return '150px' for any screens which are smaller than 'md', '250px' for screens
 * which are 'md', 'lg' or 'xl' and '400px' for screens which are '2xl'.
 *
 * DEPRECTAED (mdlg breakpoint not supported):
 * You can also pass in an array of values. The values are mapped to the current
 * breakpoints, so the following are equivalent:
 * ```
 * useBreakpointValue({
 *   base: 1,
 *   sm: 2,
 *   md: 3,
 *   lg: 4,
 *   xl: 5,
 *   2xl: 6,
 * })
 * useBreakpointValue([1, 2, 3, 4, 5, 6]);
 * ```
 *
 * If the number of elements in the array is less than the number of breakpoints,
 * the final value is used for all larger screen sizes, so these are equivalent:
 * ```
 * useBreakpointValue({
 *   base: 1,
 *   md: 2,
 * });
 * useBreakpointValue([1, 1, 2]);
 * ```
 *
 * This was deprecated after adding a new breakpoint to middle of the array,
 * causing the indexes to no longer match the breakpoint intended.
 * The array syntax does not support the mdlg breakpoint
 *
 */
export function useBreakpointValue(value) {
  var breakpoint = useBreakpoint();
  var theme = useTheme();
  if (!breakpoint) return undefined;

  /**
   * To prevent augmenting the argument passed
   */
  var values = isArray(value) ? [...value] : value;
  /**
   * This copies the third index to the fourth
   * To support bg={[]} syntax without the new mdlg breakpoint
   * base, sm, md, mdlg, lg, xl - mdlg will be ignored
   */
  if (isArray(values) && values.length > 3) {
    values.splice(3, 0, values[2]);
  }

  /**
   * Get the non-number breakpoint keys from the provided breakpoints
   */
  var breakpoints = Object.keys(theme.breakpoints).filter(isCustomBreakpoint);
  var obj = isArray(values) ? fromEntries(Object.entries(arrayToObjectNotation(values, breakpoints)).map(_ref => {
    var [key, value] = _ref;
    return [key, value];
  })) : values;
  return getClosestValue(obj, breakpoint, breakpoints);
}